import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { SelectField, SelectFieldOptions } from "@/components/SelectField/SelectField";
import { Toggle } from "@/components/Toggle/Toggle";
import { useUpdateMeMutation } from "@/pages/Private/redux/account/account.api";
import { runOrToast } from "@/utils/runOrToast";

import { SettingsSubhead, SettingsSubBlock } from "./SettingsSection";

export const AUTO_WITHDRAW_INVITES_DAY_OPTIONS = [30, 40, 50] as const;
const DEFAULT_AUTO_WITHDRAW_INVITES_DAYS = AUTO_WITHDRAW_INVITES_DAY_OPTIONS[0];

interface AutoWithdrawInvitesProps {
	autoWithdrawInvitesEnabled: boolean;
	autoWithdrawInvitesDays?: number;
}

/**
 * "Auto-withdraw" section of the LinkedIn card: a toggle + window selector that cancels
 * connection requests still pending after the chosen number of days. Renders as a subhead
 * section inside the LinkedIn card (matching the mockup) rather than as its own card.
 */
export const AutoWithdrawInvites: FunctionComponent<AutoWithdrawInvitesProps> = ({
	autoWithdrawInvitesEnabled,
	autoWithdrawInvitesDays,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);
	const tc = (key: string) => t(`account.connectedAccounts.${key}`);

	const [updateMe, { isLoading }] = useUpdateMeMutation();

	const handleToggleChange = async () => {
		const updates: Record<string, unknown> = {
			autoWithdrawInvitesEnabled: !autoWithdrawInvitesEnabled,
		};

		// Ensure a valid preset is persisted when enabling for the first time.
		if (!autoWithdrawInvitesEnabled && !autoWithdrawInvitesDays) {
			updates.autoWithdrawInvitesDays = DEFAULT_AUTO_WITHDRAW_INVITES_DAYS;
		}

		await runOrToast(() => updateMe(updates).unwrap(), t("errors.saveFailed"));
	};

	const handleDaysChange = async (value: string | number | null) => {
		await runOrToast(
			() =>
				updateMe({
					autoWithdrawInvitesDays: Number(
						value
					) as (typeof AUTO_WITHDRAW_INVITES_DAY_OPTIONS)[number],
				}).unwrap(),
			t("errors.saveFailed")
		);
	};

	const dayOptions: SelectFieldOptions = AUTO_WITHDRAW_INVITES_DAY_OPTIONS.map((days) => ({
		title: ts("autoWithdrawInvitesDaysOption").replace("{{days}}", String(days)),
		value: days,
	}));

	return (
		<div>
			<SettingsSubhead>{tc("autoWithdrawSubhead")}</SettingsSubhead>

			<SettingsSubBlock className="mt-2">
				<div className="flex items-start gap-3">
					<Toggle
						className="mt-0.5"
						disabled={isLoading}
						isChecked={autoWithdrawInvitesEnabled}
						name="auto-withdraw"
						testId="auto-withdraw-toggle"
						onChange={handleToggleChange}
					/>

					<div className="min-w-0 flex-1">
						<span className="text-sm font-semibold text-text">
							{ts("enableAutoWithdrawInvites")}
						</span>
						<p className="mt-1.5 text-[12.5px] leading-relaxed text-text-meta">
							{ts("enableAutoWithdrawInvitesDescription")}
						</p>

						{autoWithdrawInvitesEnabled && (
							<div className="mt-4 max-w-[220px]">
								<SelectField
									// The settings card clips overflow, so the list renders outside it (PREC-4463).
									portal
									hideEmptyValue
									handleSelect={handleDaysChange}
									label={ts("autoWithdrawInvitesWindow")}
									options={dayOptions}
									selectedOption={autoWithdrawInvitesDays ?? DEFAULT_AUTO_WITHDRAW_INVITES_DAYS}
								/>
							</div>
						)}
					</div>
				</div>
			</SettingsSubBlock>
		</div>
	);
};
